/*
  Kkumi promo banners — campaign-driven.

  Fetches the live campaigns from the App Proxy (/apps/rewards/campaigns, the
  same guest-safe endpoint the earn previews use) and renders, per campaign's
  banner config:
    • a top announcement bar (always LOUD) into the #kkumi-promo-top mount that
      the snippet drops in just above the header, and
    • a floating corner card (always CALM) appended to <body> (position:fixed).

  Tone is fixed by placement (handoff), so there's no tone option. Each banner
  has a live countdown to the campaign's ends_at (when enabled), an optional CTA
  button, an optional code pill, and a × dismiss that persists per-campaign in
  localStorage so it stays hidden on reload.

  SECURITY: every value here comes from the API, so all text is escaped before
  it touches innerHTML and the CTA URL is allow-listed (path or http[s] only)
  and attribute-escaped (FRONTEND.md → Security defaults).
*/
(function () {
  var STORE_KEY = 'kkumi_promo_dismissed_';

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  var esc = window.KKEsc; // canonical shared escaper (assets/kkumi-esc.js)
  // Merchant copy overrides (Theme settings → Kkumi Promo Banner) handed over by
  // snippets/kkumi-promo-banner.liquid as window.KKUMI_PROMO. Campaign content is
  // API-sourced; only this chrome (countdown units, labels, aria) is editable.
  var PROMO = window.KKUMI_PROMO || {};
  function pc(key, fallback) { return PROMO[key] != null && PROMO[key] !== '' ? PROMO[key] : fallback; }
  // Allow only a same-origin path or an http(s) URL — never javascript:/data:.
  function safeUrl(u) {
    var s = String(u || '').trim();
    if (/^\/(?!\/)/.test(s) || /^https?:\/\//i.test(s)) return s;
    return null;
  }
  // Dismissal is per CAMPAIGN + PLACEMENT, so a campaign that shows both a top
  // bar and a floating card can have each closed independently.
  function dismissed(id, placement) {
    try { return localStorage.getItem(STORE_KEY + id + ':' + placement) === '1'; } catch (e) { return false; }
  }
  function setDismissed(id, placement) {
    try { localStorage.setItem(STORE_KEY + id + ':' + placement, '1'); } catch (e) { /* private mode — session-only */ }
  }
  function clearDismissed(id, placement) {
    try { localStorage.removeItem(STORE_KEY + id + ':' + placement); } catch (e) { /* ignore */ }
  }

  function pad(n) { return String(n).padStart(2, '0'); }
  // Visible countdown units: drop the largest unit(s) once they hit zero — days
  // while ≥1 day remains, then hours under a day, minutes under an hour, seconds
  // under a minute (matches the Showdowns timer). Always keeps at least one unit.
  function visibleUnits(endsAt) {
    var diff = Math.max(0, Number(endsAt) - Date.now());
    var d = Math.floor(diff / 86400000); diff -= d * 86400000;
    var h = Math.floor(diff / 3600000); diff -= h * 3600000;
    var m = Math.floor(diff / 60000); diff -= m * 60000;
    var s = Math.floor(diff / 1000);
    var all = [{ v: d, l: pc('unit_days', 'days') }, { v: h, l: pc('unit_hrs', 'hrs') }, { v: m, l: pc('unit_min', 'min') }, { v: s, l: pc('unit_sec', 'sec') }];
    var i = 0; while (i < 3 && all[i].v === 0) i++;
    return all.slice(i);
  }
  function cellsHtml(us, loud) {
    return us.map(function (u, i) {
      return '<div class="kkpromo-cd__cell" data-loud="' + (loud ? '1' : '0') + '">' +
        '<span class="kkpromo-cd__num">' + pad(u.v) + '</span>' +
        '<span class="kkpromo-cd__lab">' + esc(u.l) + '</span></div>' +
        (i < us.length - 1 ? '<span class="kkpromo-cd__sep">:</span>' : '');
    }).join('');
  }
  function countdownHtml(endsAt, loud, compact) {
    return '<div class="kkpromo-cd" data-compact="' + (compact ? '1' : '0') + '" data-loud="' + (loud ? '1' : '0') + '" data-ends="' + Number(endsAt) + '">' + cellsHtml(visibleUnits(endsAt), loud) + '</div>';
  }
  // One code-pill style everywhere (announcement bar + floating card).
  function redeemHtml(code) {
    if (!code) return '';
    return '<span class="kkpromo-redeem">' +
      '<span class="kkpromo-redeem__k">' + esc(pc('code_label', 'code')) + '</span>' +
      '<span class="kkpromo-redeem__v">' + esc(code) + '</span></span>';
  }
  function ctaHtml(label, url, loud) {
    var safe = safeUrl(url);
    if (!label || !safe) return '';
    return '<a class="kkpromo-cta" href="' + esc(safe) + '">' + esc(label) + '</a>';
  }

  function headHtml(b) {
    var l1 = b.line1 ? esc(b.line1) + ' ' : '';
    var l2 = b.line2 ? '<em>' + esc(b.line2) + '</em>' : '';
    return l1 + l2;
  }

  function topBarHtml(c) {
    var b = c.banner;
    // Three sibling blocks of the inner row so they can reorder responsively:
    //   desktop → headline + code on the left, countdown pushed right;
    //   mobile  → stacked headline, then countdown, then code (CSS `order`).
    var lead = '<div class="kkpromo-barA__lead">' +
        '<div class="kkpromo-barA__msg">' +
          '<span class="kkpromo-barA__head">' + headHtml(b) + '</span>' +
          (b.sub ? '<span class="kkpromo-barA__sub">' + esc(b.sub) + '</span>' : '') +
        '</div></div>';
    var code = b.code ? redeemHtml(b.code) : '';
    var actions = (b.countdown && c.ends_at)
      ? '<div class="kkpromo-barA__actions">' + countdownHtml(c.ends_at, true, true) + '</div>'
      : '';
    return '<div class="kkpromo kkpromo-barA" role="region" aria-label="' + esc(pc('region_label', 'Promotion')) + '">' +
      '<button type="button" class="kkpromo-x kkpromo-barA__x" data-kkpromo-dismiss="' + esc(c.id) + '" data-kkpromo-placement="top" aria-label="' + esc(pc('dismiss_label', 'Dismiss')) + '">×</button>' +
      // .page-width aligns the content to the theme's universal page width.
      '<div class="kkpromo-barA__inner page-width">' + lead + code + actions + '</div>' +
    '</div>';
  }

  function floatCardHtml(c) {
    var b = c.banner;
    var foot = '';
    if (b.code) foot += redeemHtml(b.code);
    foot += ctaHtml(b.cta_label, b.cta_url, false);
    return '<div class="kkpromo kkpromo-card" role="region" aria-label="' + esc(pc('region_label', 'Promotion')) + '">' +
      '<button type="button" class="kkpromo-x kkpromo-card__x" data-kkpromo-dismiss="' + esc(c.id) + '" data-kkpromo-placement="float" aria-label="' + esc(pc('dismiss_label', 'Dismiss')) + '">×</button>' +
      '<div class="kkpromo-card__head">' + headHtml(b) + '</div>' +
      (b.sub ? '<p class="kkpromo-card__sub">' + esc(b.sub) + '</p>' : '') +
      (b.countdown && c.ends_at ? countdownHtml(c.ends_at, false, false) : '') +
      (foot ? '<div class="kkpromo-card__foot">' + foot + '</div>' : '') +
    '</div>';
  }

  // A banner is worth showing only if it has a headline to render.
  function hasContent(c) { return !!(c.banner && (c.banner.line1 || c.banner.line2 || c.banner.sub)); }

  function wireDismiss(root) {
    root.querySelectorAll('[data-kkpromo-dismiss]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setDismissed(btn.getAttribute('data-kkpromo-dismiss'), btn.getAttribute('data-kkpromo-placement'));
        var card = btn.closest('.kkpromo-card');
        if (card) { var h = card.parentNode; card.remove(); if (h && h.id === 'kkumi-promo-float') h.remove(); }
        else { var mount = document.getElementById('kkumi-promo-top'); if (mount) mount.innerHTML = ''; }
        renderReopen();
      });
    });
  }

  // Held so the re-open tab can re-render the same campaigns it loaded.
  var loadedCampaigns = [];

  // Any live placement that's currently dismissed → offer a way back.
  function anyDismissed() {
    return loadedCampaigns.some(function (c) {
      return (c.banner.top && dismissed(c.id, 'top')) || (c.banner.float && dismissed(c.id, 'float'));
    });
  }
  function renderReopen() {
    var existing = document.getElementById('kkumi-promo-reopen');
    if (!anyDismissed()) { if (existing) existing.remove(); return; }
    if (existing) return;
    var btn = document.createElement('button');
    btn.id = 'kkumi-promo-reopen';
    btn.type = 'button';
    btn.className = 'kkpromo-reopen';
    btn.setAttribute('aria-label', pc('reopen_aria', 'Show offers'));
    btn.innerHTML = '<span class="kkpromo-reopen__star" aria-hidden="true">✦</span><span>' + esc(pc('reopen_label', 'Offers')) + '</span>';
    btn.addEventListener('click', function () {
      loadedCampaigns.forEach(function (c) { clearDismissed(c.id, 'top'); clearDismissed(c.id, 'float'); });
      render(loadedCampaigns);
    });
    document.body.appendChild(btn);
  }

  var ticker = null;
  function startCountdowns() {
    if (ticker) return;
    ticker = setInterval(function () {
      var cds = document.querySelectorAll('.kkpromo-cd[data-ends]');
      if (!cds.length) { clearInterval(ticker); ticker = null; return; }
      cds.forEach(function (cd) {
        var us = visibleUnits(cd.getAttribute('data-ends'));
        var nums = cd.querySelectorAll('.kkpromo-cd__num');
        if (nums.length !== us.length) {
          // crossed a unit boundary (e.g. dropped below a day) → rebuild the cells
          cd.innerHTML = cellsHtml(us, cd.getAttribute('data-loud') === '1');
        } else {
          for (var i = 0; i < nums.length; i++) {
            var txt = pad(us[i].v);
            if (nums[i].textContent !== txt) nums[i].textContent = txt; // idempotent write
          }
        }
      });
    }, 1000);
  }

  function render(campaigns) {
    loadedCampaigns = (campaigns || []).filter(hasContent);

    // Top bar — show the first campaign whose top placement isn't dismissed.
    var top = loadedCampaigns.find(function (c) { return c.banner.top && !dismissed(c.id, 'top'); });
    var mount = document.getElementById('kkumi-promo-top');
    if (mount) {
      mount.innerHTML = top ? topBarHtml(top) : '';
      if (top) wireDismiss(mount);
    }

    // Floating card — likewise, independent of the top bar.
    var oldFloat = document.getElementById('kkumi-promo-float');
    if (oldFloat) oldFloat.remove();
    var floater = loadedCampaigns.find(function (c) { return c.banner.float && !dismissed(c.id, 'float'); });
    if (floater) {
      var holder = document.createElement('div');
      holder.id = 'kkumi-promo-float';
      holder.innerHTML = floatCardHtml(floater);
      document.body.appendChild(holder);
      wireDismiss(holder);
    }

    if (top || floater) startCountdowns();
    renderReopen();
  }

  function init() {
    var base = shopifyRoot().replace(/\/$/, '');
    // /campaigns is public + sitewide; read it through the shared client cache
    // (window.KKRewards) so it isn't refetched on every page. Falls back to a
    // direct fetch when the cache module isn't present.
    var p = window.KKRewards
      ? window.KKRewards.get('/campaigns', { scope: 'public' }).catch(function () { return null; })
      : fetch(base + '/apps/rewards/campaigns', { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; });
    p.then(function (data) { if (data && Array.isArray(data.campaigns)) render(data.campaigns); })
      .catch(function () { /* banners are non-critical — fail silently */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
